import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get auth token
const getAuthHeader = () => {
  const token = localStorage.getItem('opwmp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Session timeout handler - redirects to login on 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored auth data
      localStorage.removeItem('opwmp_token');
      localStorage.removeItem('opwmp_user');
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        // Store current path to redirect back after login
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        
        // Redirect to login with session expired message
        window.location.href = '/login?session_expired=true';
      }
    }
    return Promise.reject(error);
  }
);

// Get current user
export const getCurrentUser = async () => {
  const response = await axios.get(`${API}/auth/me`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Pricing
export const getPricing = async () => {
  const response = await axios.get(`${API}/pricing`);
  return response.data;
};

// ==================== FREE PLAN PROMO (START) ====================
export const checkFreePlanEligible = async (projectId = null) => {
  const params = projectId ? `?project_id=${projectId}` : '';
  const response = await axios.get(`${API}/promo/free-plan-eligible${params}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const redeemFreePlan = async (projectId) => {
  const response = await axios.post(`${API}/promo/redeem-free-plan/${projectId}`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};
// ==================== FREE PLAN PROMO (END) ====================

// States
export const getStates = async () => {
  const response = await axios.get(`${API}/states`);
  return response.data;
};

// Get councils for a specific state
export const getCouncilsForState = async (stateCode) => {
  const response = await axios.get(`${API}/states/${stateCode}/councils`);
  return response.data;
};

// Get commercial operation types for a specific state
export const getOperationTypesForState = async (stateCode) => {
  const response = await axios.get(`${API}/states/${stateCode}/operation-types`);
  return response.data;
};

// Commercial operation types (can optionally pass state)
export const getCommercialOperationTypes = async (stateCode = 'VIC') => {
  const response = await axios.get(`${API}/commercial-operation-types?state_code=${stateCode}`);
  return response.data;
};

// Bin specifications
export const getBinSpecifications = async () => {
  const response = await axios.get(`${API}/bin-specifications`);
  return response.data;
};

// Projects
export const createProject = async (projectData) => {
  const response = await axios.post(`${API}/projects`, projectData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getProjects = async () => {
  const response = await axios.get(`${API}/projects`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getProject = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Update project Step 1 data
export const updateProject = async (projectId, projectData) => {
  const response = await axios.put(`${API}/projects/${projectId}/step1`, projectData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateProjectStep2 = async (projectId, step2Data) => {
  const response = await axios.put(`${API}/projects/${projectId}/step2`, step2Data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const calculateWaste = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}/calculate-waste`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateProjectStep3 = async (projectId, step3Data) => {
  const response = await axios.put(`${API}/projects/${projectId}/step3`, step3Data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getBinConfigurations = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}/bin-configurations`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateProjectStep5 = async (projectId, step5Data) => {
  const response = await axios.put(`${API}/projects/${projectId}/step5`, step5Data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const completeStep4 = async (projectId) => {
  const response = await axios.post(`${API}/projects/${projectId}/step4-complete`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Step 6 (Refinements) and Step 7 (Payment) APIs
export const getProjectSummary = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}/summary`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateProjectStep6 = async (projectId, step6Data) => {
  const response = await axios.put(`${API}/projects/${projectId}/step6`, step6Data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Step 7 - Appendix 4 Document Uploads
export const uploadAppendix4File = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(
    `${API}/projects/${projectId}/appendix4/upload`,
    formData,
    {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

export const deleteAppendix4File = async (projectId, fileIndex) => {
  const response = await axios.delete(`${API}/projects/${projectId}/appendix4/${fileIndex}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAppendix4Files = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}/appendix4/files`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateProjectStep7 = async (projectId, step7Data) => {
  const response = await axios.put(`${API}/projects/${projectId}/step7`, step7Data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Step 8 - Review & Payment (was Step 7)
export const updateProjectStep8 = async (projectId, step8Data) => {
  const response = await axios.put(`${API}/projects/${projectId}/step8`, step8Data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createCheckoutSession = async (projectId, selectedAddons = []) => {
  const originUrl = window.location.origin;
  const response = await axios.post(`${API}/payments/create-checkout`, {
    project_id: projectId,
    origin_url: originUrl,
    selected_addons: selectedAddons,
    // Backwards compatibility
    rfi_addon: selectedAddons.includes('rfi_response')
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await axios.get(`${API}/payments/status/${sessionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const downloadProjectPDF = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}/download-pdf`, {
    headers: getAuthHeader(),
    responseType: 'blob'
  });
  return response.data;
};

// D&CWMP PDF download
export const downloadDCWMPPDF = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}/dcwmp/download-pdf`, {
    headers: getAuthHeader(),
    responseType: 'blob'
  });
  return response.data;
};

// CMP PDF download
export const downloadCMPPDF = async (projectId) => {
  const response = await axios.get(`${API}/projects/${projectId}/cmp/download-pdf`, {
    headers: getAuthHeader(),
    responseType: 'blob'
  });
  return response.data;
};

// TEMPORARY: Dev bypass payment for testing
export const devMarkProjectPaid = async (projectId) => {
  const response = await axios.post(`${API}/projects/${projectId}/dev-mark-paid`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// File uploads
export const uploadSiteImage = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(
    `${API}/projects/${projectId}/upload-site-image`,
    formData,
    {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// Get site image URL for a project
export const getSiteImageUrl = (projectId) => {
  return `${API}/projects/${projectId}/site-image`;
};

// NSW Enquiry
export const createNSWEnquiry = async (enquiryData) => {
  const response = await axios.post(`${API}/nsw-enquiry`, enquiryData);
  return response.data;
};

export const getNSWEnquiries = async () => {
  const response = await axios.get(`${API}/nsw-enquiries`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateNSWEnquiryStatus = async (enquiryId, status) => {
  const response = await axios.put(`${API}/nsw-enquiries/${enquiryId}/status?status=${status}`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteNSWEnquiry = async (enquiryId) => {
  const response = await axios.delete(`${API}/nsw-enquiries/${enquiryId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Delete project
export const deleteProject = async (projectId) => {
  const response = await axios.delete(`${API}/projects/${projectId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};
